import { apiRequest } from "@/lib/api";

export type NotificationCategory =
  | "Support"
  | "Enrollments"
  | "ID's"
  | "Bookstore"
  | "Certificate & Transcript"
  | "Verification request"
  | "Identity"
  | "Payments"
  | "Forms"
  | "Leads"
  | "Course Keys"
  | "Clinicals"
  | "Journey"
  | "Tasks & Reminders";

export type NotificationPriority = "urgent" | "high" | "medium" | "low";
export type NotificationUiStatus = "Unread" | "Read" | "Done" | "Archived";

export type AdminNotificationItem = {
  id: string;
  sourceKey: string;
  category: NotificationCategory;
  priority: NotificationPriority;
  title: string;
  description: string;
  person: string;
  program?: string;
  amount?: string;
  order?: string;
  fulfillment?: string;
  items?: string;
  documentType?: string;
  form?: string;
  clinicalStatus?: string;
  requestType?: string;
  time: string;
  createdAt: string;
  dateGroup: "Today" | "This Week" | "Older";
  status: NotificationUiStatus;
  target: string;
  href: string;
  action: string;
  team: string;
  details: Record<string, string>;
};

export type NotificationSummary = {
  unread: number;
  urgent: number;
  high: number;
  done: number;
  byCategory: Record<string, number>;
};

export type NotificationPreferences = {
  mutedCategories: string[];
  soundOnUrgent: boolean;
};

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  mutedCategories: [],
  soundOnUrgent: true,
};

type ListResponse = {
  statusCode: number;
  message: string;
  data: {
    items: AdminNotificationItem[];
    summary: NotificationSummary;
  };
};

type SummaryResponse = {
  statusCode: number;
  message: string;
  data: { summary: NotificationSummary };
};

type PreferencesResponse = {
  statusCode: number;
  message: string;
  data: {
    preferences: NotificationPreferences;
    categories?: NotificationCategory[];
  };
};

export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  "Support",
  "Enrollments",
  "ID's",
  "Bookstore",
  "Certificate & Transcript",
  "Verification request",
  "Identity",
  "Payments",
  "Forms",
  "Leads",
  "Course Keys",
  "Clinicals",
  "Journey",
  "Tasks & Reminders",
];

/** Map notification categories → sidebar route prefixes for badge counts */
export const SIDEBAR_BADGE_CATEGORIES: Record<string, NotificationCategory[]> = {
  "/tickets": ["Support"],
  "/queries": ["Leads"],
  "/bookstore/orders": ["Bookstore"],
  "/transcripts/requests": ["Certificate & Transcript"],
  "/education-verification/requests": ["Verification request"],
  "/name-change/requests": ["Identity"],
  "/certifications": ["Clinicals", "Certificate & Transcript"],
  "/f": ["Forms"],
  "/payments": ["Payments"],
  "/payments/checkout-tracking": ["Payments"],
  "/course-keys": ["Course Keys"],
  "/journey-flows": ["Journey"],
  "/tasks": ["Tasks & Reminders"],
  "/reminders": ["Tasks & Reminders"],
  "/users": ["ID's"],
  "/enrollments": ["Enrollments"],
  "/notifications": [], // total shown via bell
};

export const notificationApi = {
  list(params?: {
    category?: string;
    priority?: string;
    status?: string;
    search?: string;
  }) {
    const qs = new URLSearchParams();
    if (params?.category && params.category !== "All") qs.set("category", params.category);
    if (params?.priority && params.priority !== "All") qs.set("priority", params.priority);
    if (params?.status && params.status !== "All") qs.set("status", params.status);
    if (params?.search) qs.set("search", params.search);
    const query = qs.toString();
    return apiRequest<ListResponse>(
      `/api/admin/notifications${query ? `?${query}` : ""}`
    );
  },

  summary() {
    return apiRequest<SummaryResponse>("/api/admin/notifications/summary");
  },

  getPreferences() {
    return apiRequest<PreferencesResponse>("/api/admin/notifications/preferences");
  },

  updatePreferences(body: Partial<NotificationPreferences>) {
    return apiRequest<PreferencesResponse>("/api/admin/notifications/preferences", {
      method: "PUT",
      body,
    });
  },

  markRead(sourceKeys: string[]) {
    return apiRequest<{ statusCode: number; message: string }>("/api/admin/notifications/mark-read", {
      method: "POST",
      body: { sourceKeys },
    });
  },

  markDone(
    sourceKeys: string[],
    snapshots?: Array<{ sourceKey: string; category?: string; title?: string; person?: string }>
  ) {
    return apiRequest<{ statusCode: number; message: string }>("/api/admin/notifications/mark-done", {
      method: "POST",
      body: { sourceKeys, snapshots },
    });
  },

  archive(
    sourceKeys: string[],
    snapshots?: Array<{ sourceKey: string; category?: string; title?: string; person?: string }>
  ) {
    return apiRequest<{ statusCode: number; message: string }>("/api/admin/notifications/archive", {
      method: "POST",
      body: { sourceKeys, snapshots },
    });
  },
};

export function snapshotFromItem(item: AdminNotificationItem) {
  return {
    sourceKey: item.sourceKey,
    category: item.category,
    title: item.title,
    person: item.person,
  };
}

export function countForRoutes(
  summary: NotificationSummary | null | undefined,
  categories: NotificationCategory[]
): number {
  if (!summary?.byCategory || !categories.length) return 0;
  return categories.reduce((sum, cat) => sum + (summary.byCategory[cat] || 0), 0);
}
